import { FC, useEffect } from 'react';

import { Formik } from 'formik';
import { BaseSelect, FormBodyContainer } from 'wbs-components-lib';

import { VipTaskItemStatusesEnum } from '@shared/constants';

import { useGetAllVipTasksQuery, useGetVipTaskItemsByTaskIdAndUserIdQuery } from '@services';

import { VipUserTaskFormProps } from './types';
import { prepareTaskItemStatusesToBaseSelect, prepareTaskItemsToBaseSelect, prepareTasksToBaseSelect } from './utils';
import { vipTaskItemValidationSchema } from './validationSchema';

export const VipUserTaskForm: FC<VipUserTaskFormProps> = ({
  initialValues,
  userId,
  onSubmit,
  isSubmitting = false,
  submitButtonCaption = 'Update',
}) => {
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={vipTaskItemValidationSchema}
      enableReinitialize
    >
      {({ submitForm, values, handleBlur, setFieldValue, errors, touched }) => {
        const {
          vipTask: vipTaskValue,
          vipTaskItem: vipTaskItemValue,
          vipTaskItemStatus: vipTaskItemStatusValue,
        } = values;
        const {
          vipTask: vipTaskError,
          vipTaskItem: vipTaskItemError,
          vipTaskItemStatus: vipTaskItemStatusError,
        } = errors;
        const {
          vipTask: isVipTaskTouched,
          vipTaskItem: isVipTaskItemTouched,
          vipTaskItemStatus: isVipTaskItemStatusTouched,
        } = touched;

        const { data: vipTasks = [] } = useGetAllVipTasksQuery(undefined, {
          refetchOnMountOrArgChange: true,
        });
        const { data: vipTaskItems = [] } = useGetVipTaskItemsByTaskIdAndUserIdQuery(
          {
            vipTaskId: vipTaskValue,
            userId,
          },
          {
            refetchOnMountOrArgChange: true,
            skip: !vipTaskValue,
          }
        );

        const onFormSubmit = async () => {
          await submitForm();
        };

        const currentVipTaskItem = vipTaskItems.find((item) => item.id === vipTaskItemValue);

        useEffect(() => {
          if (currentVipTaskItem !== undefined) {
            setFieldValue('vipTaskItemStatus', currentVipTaskItem.status);
          }
        }, [vipTaskItems, vipTaskItemValue]);

        const isVipTaskError = isVipTaskTouched && !!vipTaskError;
        const isVipTaskItemError = isVipTaskItemTouched && !!vipTaskItemError;
        const isVipTaskItemStatusError = !isVipTaskItemStatusTouched && !!vipTaskItemStatusError;

        const isInitialStatusInactive = currentVipTaskItem?.status === VipTaskItemStatusesEnum.INACTIVE;
        const isInitialStatusDone = currentVipTaskItem?.status === VipTaskItemStatusesEnum.DONE;
        const isInitialStatusChangedFromPendingToInactive =
          currentVipTaskItem?.status === VipTaskItemStatusesEnum.PENDING &&
          vipTaskItemStatusValue === VipTaskItemStatusesEnum.INACTIVE;
        const isInitialStatusNotChanged =
          currentVipTaskItem?.status === VipTaskItemStatusesEnum.PENDING &&
          vipTaskItemStatusValue === VipTaskItemStatusesEnum.PENDING;
        const isVipTaskItemStatusFieldDisabled =
          isInitialStatusInactive ||
          isInitialStatusDone ||
          isInitialStatusChangedFromPendingToInactive ||
          isInitialStatusNotChanged;

        return (
          <FormBodyContainer
            isSubmitting={isSubmitting}
            onSubmit={onFormSubmit}
            submitButtonCaption={submitButtonCaption}
            isSubmitButtonDisabled={isVipTaskItemStatusFieldDisabled}
          >
            <BaseSelect
              // @ts-ignore
              id="vipTask"
              name="vipTask"
              errorText={vipTaskError}
              isError={isVipTaskError}
              onBlur={handleBlur}
              items={prepareTasksToBaseSelect(vipTasks)}
              handleValue="id"
              handleChange={(value: string) => setFieldValue('vipTask', value)}
              value={vipTaskValue}
              caption="Vip Task"
              label="Select vip task"
            />
            <BaseSelect
              // @ts-ignore
              id="vipTaskItem"
              name="vipTaskItem"
              errorText={vipTaskItemError}
              isError={isVipTaskItemError}
              onBlur={handleBlur}
              items={prepareTaskItemsToBaseSelect(vipTaskItems)}
              handleValue="id"
              handleChange={(value: string) => setFieldValue('vipTaskItem', value)}
              value={vipTaskItemValue}
              caption="Vip Task Item"
              label="Select vip task item"
            />
            <BaseSelect
              // @ts-ignore
              id="vipTaskItemStatus"
              name="vipTaskItemStatus"
              errorText={vipTaskItemStatusError}
              isError={isVipTaskItemStatusError}
              onBlur={handleBlur}
              disabled={isInitialStatusInactive || isInitialStatusDone}
              items={prepareTaskItemStatusesToBaseSelect()}
              handleValue="id"
              handleChange={(value: string) => setFieldValue('vipTaskItemStatus', value)}
              value={vipTaskItemStatusValue.toString()}
              caption="Vip Task Item Status"
              label="Select vip task item status"
            />
          </FormBodyContainer>
        );
      }}
    </Formik>
  );
};
